import { useMemo, useState } from 'react';
import { Box, Divider, HStack, ScrollView } from 'native-base';
import { find, map, partition, sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Text } from '@pimm/base';
import { HubStatDto } from '@pimm/services/lib/sms-workforce';
import { useUsersWithSiteKeys } from '@app/features/app/context';
import { ResourceLoader } from '@app/components/shared';
import { formatName } from '@app/utils/string-formatter';
import { useGetHubStats } from '../hooks';
import { LearningHubCard } from './learning-hub-card';
import { ExtendedHubStatDto, LearningHubPreview } from './learning-hub-preview';

type LearningHubProps = {
  queryHubStats: ReturnType<typeof useGetHubStats>;
  search?: string;
};

export const LearningHub = ({ queryHubStats, search }: LearningHubProps) => {
  const [hubStatFocus, setHubStatFocus] = useState<HubStatDto>();
  const { t } = useTranslation();
  const usersWithSiteKey = useUsersWithSiteKeys(state => state.users);

  const [hubStats, smsStore] = useMemo(() => {
    const data = (queryHubStats.data ?? []) as HubStatDto[];

    const filteredData = search?.length
      ? data.filter(u => {
          const name = formatName(u?.firstName, u?.lastName).toLowerCase();
          return name.includes(search);
        })
      : data;

    return partition(filteredData || [], ({ role }) => role?.toLowerCase() !== 'sms store').map(group =>
      sortBy(group, ['firstName', 'lastName']),
    );
  }, [queryHubStats.data, search]);

  const handlePressEmployee = (hubStat: HubStatDto) => () => {
    setHubStatFocus(hubStat);
  };

  return (
    <ResourceLoader w="full" h="full" isLoading={queryHubStats.isLoading}>
      <HStack h="full">
        <ScrollView flex={1} h="full" bg="gray.50">
          <HStack h="full" w="full" flexWrap="wrap" p={2}>
            {map(hubStats, _hubStat => {
              const isSelected = _hubStat?.id === hubStatFocus?.id;
              const isPinSet = find<ExtendedHubStatDto>(usersWithSiteKey, { userName: _hubStat?.userName })?.pinIsSet;

              return (
                <LearningHubCard
                  key={`learning-hub.${_hubStat.id}`}
                  _container={{ w: '1/3' }}
                  hubStat={_hubStat}
                  isSelected={isSelected}
                  isPinSet={isPinSet}
                  onPress={handlePressEmployee(_hubStat)}
                />
              );
            })}
          </HStack>
        </ScrollView>

        <Divider orientation="vertical" bgColor="gray.200" />

        <Box w="2/5" bg="white">
          {hubStatFocus ? (
            <LearningHubPreview key={hubStatFocus.id} hubStat={hubStatFocus} />
          ) : (
            <Box flex={1} alignItems="center" justifyContent="center">
              <Text size="xl" fontWeight={600} color="gray.600">
                {t('app_insights:select_user_to_see_more_details')}
              </Text>
            </Box>
          )}
        </Box>
      </HStack>
    </ResourceLoader>
  );
};
